import React from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  People as PeopleIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import type { DashboardChartFilters } from '../../types/dashboardStats';
import { DASHBOARD_BRAND_PRIMARY } from '../../theme/dashboardBrand';

type Props = {
  filters: DashboardChartFilters;
  disabled?: boolean;
};

const KpiCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardKpiCards: React.FC<Props> = ({ filters, disabled }) => {
  const { data } = useQuery({
    queryKey: ['dashboard-summary', filters.date_from, filters.date_to, filters.city ?? ''],
    queryFn: () => apiService.getDashboardSummary(filters),
    enabled: !disabled,
  });

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          title="Taux de résolution"
          value={`${(data?.resolution_rate ?? 0).toLocaleString('fr-FR')}%`}
          subtitle={`${data?.announcements_resolved ?? 0} / ${data?.announcements_total ?? 0} annonces`}
          icon={<CheckCircleIcon />}
          color="#22c55e"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          title="Matchings"
          value={(data?.matchings_total ?? 0).toLocaleString('fr-FR')}
          subtitle={`${(data?.confirmation_rate ?? 0).toLocaleString('fr-FR')}% confirmés`}
          icon={<HubIcon />}
          color={DASHBOARD_BRAND_PRIMARY}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          title="CA livraisons"
          value={`${(data?.delivery_revenue_eur ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}
          subtitle={`${data?.delivery_payments_total ?? 0} paiement${(data?.delivery_payments_total ?? 0) > 1 ? 's' : ''}`}
          icon={<LocalShippingIcon />}
          color="#0ea5e9"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          title="Nouveaux utilisateurs"
          value={(data?.new_users ?? 0).toLocaleString('fr-FR')}
          icon={<PeopleIcon />}
          color="#8b5cf6"
        />
      </Grid>
    </Grid>
  );
};

export default DashboardKpiCards;
